import React, { JSX, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Center, Modal, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';

import { getApplicationUsers } from '../../../../api/applicationUser';
import QrCodeScanner from '../../../../common/components/QrCodeScanner/QrCodeScanner';
import { RadioCard } from '../../../../common/components/RadioCard';
import HandScanner from '../../../../common/components/Scanner/HandScanner';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import { ItemAssignmentScannersModes, ItemAssignmentSelectMode } from './types';

export type ScanUserIdModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ScanUserIdModal = ({ isOpen, onClose }: ScanUserIdModalProps): JSX.Element => {
  const t = useTranslations();
  const { applicationUser, quickContact, setApplicationUser } = useScannedRelevosContext();

  const [selected, setSelected] = useState<string>(ItemAssignmentSelectMode.CAMERA_SCAN);
  const isClientSelected = useMemo(() => applicationUser || quickContact, [applicationUser, quickContact]);

  const showError = useCallback(
    (error: AxiosError) => {
      if (error.response) {
        toast.error(
          <b>{t(error.response.status === StatusCodes.NOT_FOUND ? 'client_not_found' : 'something_went_wrong')}</b>,
        );
      }
    },
    [t],
  );

  const handleScan = useCallback(
    async (uniqueId: string | null) => {
      if (!uniqueId || isClientSelected) return;
      try {
        const applicationUsers = await getApplicationUsers(uniqueId);
        if (applicationUsers.length === 1) {
          setApplicationUser(applicationUsers[0]);
          onClose();
        }
      } catch (e) {
        showError(e as AxiosError);
      }
    },
    [isClientSelected, onClose, setApplicationUser, showError],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent p={4} borderRadius="2xl">
        <ModalHeader>
          <Center>{t('scan_user_id_modal_title')}</Center>
        </ModalHeader>
        <ModalCloseButton />
        <Box px={2} pb={4} textAlign={'center'}>
          <RadioCard
            options={ItemAssignmentScannersModes}
            name="ScanUserIdMode"
            defaultValue={selected}
            onChange={setSelected}
            width={'100%'}
          />
        </Box>
        <Box pb={2}>
          {selected === 'item_select_mode_scan_items' ? (
            <Center>
              <QrCodeScanner onScan={handleScan} />
            </Center>
          ) : (
            <HandScanner
              onScan={handleScan}
              description={t('hand_scanner_scan_user_description')}
              connectedBg="beige.50"
            />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default ScanUserIdModal;
